import create from 'zustand';
import produce from 'immer';
import arrayMove from 'array-move';
import { nanoid } from 'nanoid';

import DayJs from 'utils/Dayjs';
import { getClosestFirstSeptember, getClosestLastMay } from 'utils/Helpers';
import { generateStudyPlan, generatePaymentPlan } from 'utils/StudyPlan';

const USER_CREATE_INITIAL = {
	user: {
		avatar: '',
		name: '',
		surname: '',
		email: '',
		gender: '',
		personalCode: '',
		phoneNumber: '',
		role: 'employee',
		status: 'active',
		address: {
			country: '',
			city: '',
			street: '',
			zip: ''
		}
	},
	edited: false
};

export const useUserCreateStore = create(set => ({
	...USER_CREATE_INITIAL,

	// Handle state change
	onChange: ({ name, value }) => set(produce(state => {
		state.edited = true;

		// Handle role change to admin
		if (name === 'role' && (value === 'admin' || value === 'employee')) {
			// eslint-disable-next-line
			const { grades, courses, parents, children, payments, ...rest } = state.user;
			state.user = rest;
		}

		// Handle role change to teacher
		if (name === 'role' && value === 'teacher') {
			// eslint-disable-next-line
			const { grades, parents, children, payments, ...rest } = state.user;
			state.user = rest;
			state.user.courses = [];
		}

		// Handle role change to student
		if (name === 'role' && value === 'student') {
			// eslint-disable-next-line
			const { courses, children, ...rest } = state.user;
			state.user = rest;
			state.user.grades = [];
			state.user.parents = [];
		}

		// Handle role change to parent
		if (name === 'role' && value === 'parent') {
			// eslint-disable-next-line
			const { grades, courses, parents, payments, ...rest } = state.user;
			state.user = rest;
			state.user.children = [];
		}

		state.user[name] = value;
	})),

	// Handle address state change
	onChangeAddress: ({ name, value }) => set(produce(state => {
		state.user.address[name] = value;
		state.edited = true;
	})),

	// Handle teacher's course addition
	onAddTeacherCourse: (course) => set(produce(state => {
		state.user.courses.push(course);
		state.edited = true;
	})),

	// Handle teacher's course removal
	onRemoveTeacherCourse: (id) => set(produce(state => {
		state.user.courses = state.user.courses.filter(course => course.id !== id);
		state.edited = true;
	})),

	// Handle parent's child addition
	onAddParentChild: (child) => set(produce(state => {
		state.user.children.push(child);
		state.edited = true;
	})),

	// Handle parent's child removal
	onRemoveParentChild: (id) => set(produce(state => {
		state.user.children = state.user.children.filter(child => child.id !== id);
		state.edited = true;
	})),

	// Handle student's grade addition
	onAddStudentGrade: (grade) => set(produce(state => {
		// Get start and end date for each grade
		const startDate = getClosestFirstSeptember(state.user.grades.length);
		const endDate = getClosestLastMay(startDate);

		state.user.grades.push({
			...grade,
			startDate: startDate,
			endDate: endDate,
			state: state.user.grades.length ? 'assigned' : 'active',
			courses: [],
			payments: [],
			marks: [],
			warrants: []
		});
		state.edited = true;
	})),

	// Handle student's grade removal
	onRemoveStudentGrade: (id) => set(produce(state => {
		state.user.grades = state.user.grades.filter(grade => grade.id !== id);
		state.edited = true;
	})),

	// Handle student's grade state change
	onChangeStudentGrade: (id, { name, value }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === id);
		grade[name] = value;
		state.edited = true;
	})),

	// Handle student's blank selection
	onSelectStudentBlank: (gradeId, courses) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.courses = courses.map(course => ({
			...course,
			startDate: '',
			endDate: '',
			exam: course.exam ? {
				...course.exam,
				startDate: '',
				endDate: '',
				state: '',
				mark: ''
			} : undefined
		}));
		state.edited = true;
	})),

	// Handle student's course addition
	onAddStudentCourse: (gradeId, course) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.courses.push({
			...course,
			startDate: '',
			endDate: '',
			exam: course.exam ? {
				...course.exam,
				startDate: '',
				endDate: '',
				state: '',
				mark: ''
			} : undefined
		});
		state.edited = true;
	})),

	// Handle student's course removal
	onRemoveStudentCourse: (gradeId, courseId) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.courses = grade.courses.filter(c => c.id !== +courseId);
		state.edited = true;
	})),

	// Handle student's course state change
	onChangeStudentCourse: (gradeId, courseId, { name, value }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		const course = grade.courses.find(c => c.id === +courseId);

		// Update course value
		course[name] = value;

		// Update exam's value
		if (course.exam) course.exam[name] = value;

		// Check if selected startDate and its the first day of the month
		if (name === 'startDate' && DayJs(value).date() === 1) {
			// Calculate last day of the month
			const endDate = DayJs(value).endOf('month').endOf('day').toISOString();

			// Update course's end date
			course.endDate = endDate;

			// Update assigned exam's end date
			if (course.exam) course.exam.endDate = endDate;
		}

		state.edited = true;
	})),

	// Handle student's exam state change
	onChangeStudentExam: (gradeId, courseId, { name, value }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		const course = grade.courses.find(c => c.id === +courseId);
		course.exam[name] = value;
		state.edited = true;
	})),

	// Handle student's course sorting
	onSortStudentCourse: (gradeId, { oldIndex, newIndex }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.courses = arrayMove(grade.courses, oldIndex, newIndex);
		state.edited = true;
	})),

	// Handle student's grade plan generation
	onGenerateStudentPlan: (gradeId) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		// grade.courses = studyPlanGenerator(grade);
		grade.courses = generateStudyPlan(grade.startDate, grade.endDate, grade.courses);
		state.edited = true;
	})),

	// Handle student's payment addition
	onAddStudentPayment: (gradeId, payment) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.payments.push({
			key: nanoid(),
			gradeId: gradeId,
			type: payment.type,
			state: payment.state,
			amount: payment.amount,
			deadlineAt: payment.deadlineAt
		});
		state.edited = true;
	})),

	// Handle student's payment removal
	onRemoveStudentPayment: (gradeId, paymentKey) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.payments = grade.payments.filter(m => m.key !== paymentKey);
		state.edited = true;
	})),

	onChangeStudentPayment: (gradeId, paymentKey, { name, value }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		const payment = grade.payments.find(p => p.key === paymentKey);
		payment[name] = value;
		state.edited = true;
	})),

	// Handle student's grade payment generation
	onGenerateStudentPayments: (gradeId, details) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		// grade.payments = paymentPlanGenerator(grade, details);
		grade.payments = generatePaymentPlan(grade.startDate, grade.endDate, details);
	})),

	// Handle student's mark addition
	onAddStudentMark: (gradeId, mark) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.marks.push({
			key: nanoid(),
			type: mark.type,
			mark: mark.mark,
			subjectId: mark?.subject?.id,
			subject: mark.subject
		});
		state.edited = true;
	})),

	// Handle student's mark removal
	onRemoveStudentMark: (gradeId, markKey) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.marks = grade.marks.filter(m => m.key !== markKey);
		state.edited = true;
	})),

	// Handle student's warrant addition
	onAddStudentWarrant: (gradeId, warrant) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.warrants.push({
			key: nanoid(),
			name: warrant.name
		});
		state.edited = true;
	})),

	// Handle student's warrant removal
	onRemoveStudentWarrant: (gradeId, warrantKey) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		grade.warrants = grade.warrants.filter(w => w.key !== warrantKey);
		state.edited = true;
	})),

	// Handle student's warrant state change
	onChangeStudentWarrant: (gradeId, warrantKey, { name, value }) => set(produce(state => {
		const grade = state.user.grades.find(g => g.id === +gradeId);
		const warrant = grade.warrants.find(w => w.key === warrantKey);
		warrant[name] = value;
		state.edited = true;
	})),

	// Handle student's parent addition
	onAddStudentParent: (parent) => set(produce(state => {
		state.user.parents.push(parent);
		state.edited = true;
	})),

	// Handle student's parent removal
	onRemoveStudentParent: (id) => set(produce(state => {
		state.user.parents = state.user.parents.filter(parent => parent.id !== id);
		state.edited = true;
	})),

	// Reset store to initial state
	onReset: () => set({
		...USER_CREATE_INITIAL
	})
}));